// #region: Class definition *********************************************************************/
/**
 * Panzoom - A library for panning and zooming elements using CSS transforms.
 *
 * Based on panzoom 4.6.0. (https://github.com/timmywil/panzoom)
 */
window.Panzoom = class {

    // #region: Private Fields *******************************************************************/
    #downEvent;
    #elem;
    #isPanning;
    #lastTimestamp = performance.now();
    #moveEvent;
    #options;
    #origX;
    #origY;
    #parent;
    #paused;
    #pointers = [];
    #scale = 1;
    #startClientX;
    #startClientY;
    #startDistance = 0;
    #startScale;
    #touchesById = new Map();
    #upEvents;
    #wheelEndTimer = null;
    #x = 0;
    #y = 0;
    // #endregion ********************************************************************************/

    // #region: Static Data **********************************************************************/
    /**
     * Debounce window (ms) after the last wheel-pan event before a synthetic `panzoomend` is fired. Wheel panning streams discrete events with no natural
     * "up", so consumers that refresh on `panzoomend` (e.g. tile reload) would never run while scrolling. Firing once the stream settles makes wheel
     * panning behave like a pointer drag without spamming the consumer mid-scroll.
     * @type {number}
     * @static
     */
    static WHEEL_PAN_END_DELAY = 150;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Creates a new panzoom instance.
     * @param {HTMLElement} elem - The element to apply panzoom to.
     * @param {Object} options - Options to customize behavior.
     */
    constructor(elem, options = {}) {
        // Determine which event types to use based on browser capabilities.
        let downEvent = 'mousedown',
            moveEvent = 'mousemove',
            upEvents = ['mouseup', 'mouseleave'];

        if (typeof window !== 'undefined') {
            if (typeof window.PointerEvent === 'function') {
                downEvent = 'pointerdown';
                moveEvent = 'pointermove';
                upEvents = ['pointerup', 'pointerleave', 'pointercancel'];
            }
            else if (typeof window.TouchEvent === 'function') {
                downEvent = 'touchstart';
                moveEvent = 'touchmove';
                upEvents = ['touchend', 'touchcancel'];
            }
        }

        // Store event types for cleanup in destroy method.
        this.#downEvent = downEvent;
        this.#moveEvent = moveEvent;
        this.#upEvents = upEvents;

        // The elements.
        this.#elem = elem;
        this.#parent = elem.parentNode;

        // Merge default options with the provided one.
        this.#options = {
            ...{
                contain: false,
                duration: 200,
                easing: 'ease-in-out',
                maxScale: 4,
                minScale: 0.125,
                pinchSensitivity: 0.5,
                pinchTransitionDuration: 150,
                startScale: 1,
                startX: 0,
                startY: 0,
                step: 0.3,
                useParent: false,
                wheelPanSpeed: 0.6,
                wheelZoomFactor: 0.1
            },
            ...options
        };

        // Store original dimensions for scaling.
        const rect = this.#elem.getBoundingClientRect();
        this.#elem.dataset.originalWidth = rect.width;
        this.#elem.dataset.originalHeight = rect.height;

        // Initialize to start position.
        this.zoom(this.#options.startScale);

        // Add event handlers.
        this.listen();
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Identifies class.
     * @returns {string} - The string representation of this class.
     */
    toString() {
        return '[Panzoom]';
    }

    static toString() {
        return '[Panzoom]';
    }

    /** 
     * Identifies the version.
     * @returns {string} - The version number of this class.
     */
    static version() {
        return '1.0.0';
    }
    // #endregion ********************************************************************************/

    // #region: Lifecycle events *****************************************************************/
    /**
     * Removes all event handlers and cleans up.
     */
    destroy() {
        // Cancel any pending wheel-pan end so it can't fire against a torn-down instance.
        if (this.#wheelEndTimer !== null) {
            clearTimeout(this.#wheelEndTimer);
            this.#wheelEndTimer = null;
        }

        const targetElement = this.#options.useParent ? this.#parent : this.#elem;

        targetElement.removeEventListener(this.#downEvent, this.handleDown);
        targetElement.removeEventListener('wheel', this.handleWheel);
        document.removeEventListener(this.#moveEvent, this.handleMove);

        this.#upEvents.forEach((eventName) => {
            document.removeEventListener(eventName, this.handleUp);
        });
    }

    /**
     * Adds all event handlers.
     */
    listen() {
        // Add 'down' event listener.
        const targetElement = this.#options.useParent ? this.#parent : this.#elem;
        targetElement.addEventListener(this.#downEvent, this.handleDown);

        // Add wheel event listener.
        const wheelTarget = this.#options.useParent ? this.#parent : this.#elem;
        wheelTarget.addEventListener('wheel', this.handleWheel, { passive: false });

        // Add 'up' event listener.
        this.#upEvents.forEach((eventName) => {
            document.addEventListener(eventName, this.handleUp, { passive: true });
        });
    }
    // #endregion ********************************************************************************/

    // #region: Events ***************************************************************************/
    /**
     * Handle pointer/touch/mouse down.
     * @param {Event} event - Pointer event.
     */
    handleDown = (event) => {
        // If the event target or any of its ancestors has the 'disable-panzoom' class, then this event should not initiate a panzoom action.
        if (event.target.closest('.disable-panzoom')) {
            return;
        }

        // Only start new gestures if we don't already have an active gesture with multiple pointers.
        if (this.#isPanning && this.#pointers.length >= 2) {
            event.preventDefault();
            return;
        }

        this.#addPointer(this.#pointers, event);
        this.#isPanning = true;

        event.preventDefault();
        event.stopPropagation();

        this.#origX = this.#x;
        this.#origY = this.#y;

        this.#trigger('panzoomstart', { originalEvent: event, scale: this.#scale, x: this.#x, y: this.#y });

        // Get the starting point (middle of fingers or cursor position).
        const point = this.#getMiddle(this.#pointers);

        this.#startClientX = point.clientX;
        this.#startClientY = point.clientY;

        // Explicitly reset pinch baseline on new two-finger gesture.
        if (this.#pointers.length === 2) {
            this.#startDistance = this.#getDistance(this.#pointers);
            this.#startScale = this.#scale;
            this.#lastTimestamp = performance.now();
        }
        else {
            this.#startScale = this.#scale;
            this.#startDistance = this.#getDistance(this.#pointers);
        }

        // Listen for the move event with passive:false to allow preventDefault.
        document.addEventListener(this.#moveEvent, this.handleMove, { passive: false });
    };

    /**
     * Handle pointer/touch/mouse move for panning and pinch zooming.
     * @param {Event} event - Pointer event.
     */
    handleMove = (event) => {
        if (!this.#isPanning ||
            this.#origX === undefined ||
            this.#origY === undefined ||
            this.#startClientX === undefined ||
            this.#startClientY === undefined) {
            return;
        }

        // For touch events, update by matching touch identifiers.
        if (event.touches) {
            // Guard for non-touch paths.
            if (!event.touches || event.touches.length < 1) {
                return;
            }

            // Update touchesById Map for O(1) lookup.
            for (const touch of event.touches) {
                const pointer = this.#touchesById.get(touch.identifier);
                if (pointer) {
                    pointer.clientX = touch.clientX;
                    pointer.clientY = touch.clientY;
                }
            }
        }
        else {
            this.#updatePointer(this.#pointers, event);
        }

        const current = this.#getMiddle(this.#pointers);
        const hasMultiple = this.#pointers.length > 1;

        // Check if the scaled element is smaller than the container.
        const rect = this.#elem.getBoundingClientRect();
        const parentRect = this.#parent.getBoundingClientRect();
        const isSmallerThanContainer = rect.width <= parentRect.width && rect.height <= parentRect.height;

        // Only set cursor to 'move' if the element is larger than the container.
        if (!isSmallerThanContainer) {
            (this.#options.useParent ? this.#parent : this.#elem).style.cursor = 'move';
        }

        // Handle pinch zooming.
        if (hasMultiple) {
            const currentDistance = this.#getDistance(this.#pointers);

            // Guard against zero/NaN pinch distances.
            if (!isFinite(currentDistance) || currentDistance < 1 || !isFinite(this.#startDistance) || this.#startDistance < 1) {
                return;
            }

            // Calculate target scale based on distance ratio.
            const distanceRatio = currentDistance / this.#startDistance;
            const targetScale = this.#startScale * distanceRatio;

            // Time-based smoothing with delta-time.
            const now = performance.now();
            const deltaTime = Math.min(0.05, (now - this.#lastTimestamp) / 1000);
            const smoothingConstant = 1 - Math.pow(0.0001, deltaTime);
            const smoothedScale = this.#scale + ((targetScale - this.#scale) * smoothingConstant);

            // Clamp scale to limits.
            const constrainedScale = Math.min(smoothedScale, Math.max(this.#options.minScale, this.#options.maxScale));

            this.#lastTimestamp = now;

            // Apply the new scale with focal point at the pinch center to keep the content under the fingers stationary.
            this.zoom(constrainedScale, {
                focal: {
                    clientX: current.clientX,
                    clientY: current.clientY
                }
            }, event);
        }
        else if (!isSmallerThanContainer) {
            // Only pan if the element is larger than the container.
            const dx = current.clientX - this.#startClientX;
            const dy = current.clientY - this.#startClientY;

            this.pan(this.#origX + dx, this.#origY + dy, event);
        }
    };

    /**
     * Handle pointer/touch/mouse up.
     * @param {Event} event - Pointer event.
     */
    handleUp = (event) => {
        // Remove move event.
        document.removeEventListener(this.#moveEvent, this.handleMove);

        // Reset cursor.
        (this.#options.useParent ? this.#parent : this.#elem).style.cursor = 'default';

        // Remove the pointer.
        this.#removePointer(this.#pointers, event);

        // Trigger panzoomend.
        this.#trigger('panzoomend', { originalEvent: event, scale: this.#scale, x: this.#x, y: this.#y });

        // Reset state.
        this.#resetState();
    };

    /**
     * Handle wheel events for panning and zooming.
     * @param {WheelEvent} event - The wheel event.
     */
    handleWheel = (event) => {
        // Prevent default browser behavior.
        event.preventDefault();

        if (event.ctrlKey) {
            // Store pre-zoom scale to detect changes.
            const preZoomScale = this.#scale;

            // Handle zooming with wheel.
            const delta = event.deltaY;
            const direction = delta < 0 ? 1 : -1;
            const zoomFactor = Math.exp(direction * this.#options.wheelZoomFactor);

            // Calculate new scale.
            let newScale = this.#scale * zoomFactor;

            // Pre-constrain newScale .
            newScale = Math.min(newScale, this.#options.maxScale);

            // Apply zoom with focal point.
            this.zoom(newScale, {
                focal: {
                    clientX: event.clientX,
                    clientY: event.clientY
                }
            }, event);

            // Trigger panzoomend after wheel zoom completes to allow quality re-render.
            if (Math.abs(this.#scale - preZoomScale) > 0.001) {
                this.#trigger('panzoomend', { originalEvent: event, scale: this.#scale, x: this.#x, y: this.#y });
            }
        }
        else {
            // Handle panning with wheel.
            const speedFactor = this.#options.wheelPanSpeed;
            const deltaX = event.deltaX * speedFactor;
            const deltaY = event.deltaY * speedFactor;

            // Apply pan relative to current position.
            this.pan(this.#x - deltaX, this.#y - deltaY, event);

            // Wheel panning has no pointer-up to mark its end, so debounce a synthetic `panzoomend` for the moment scrolling settles. Without it,
            // consumers that refresh on `panzoomend` (e.g. tile reload) only update after a pointer drag, never while scrolling.
            this.#scheduleWheelPanEnd(event);
        }
    };
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Get current options.
     * @returns {Object} - Copy of current options.
     */
    getOptions() {
        const clone = {};
        for (const key in this.#options) {
            if (Object.prototype.hasOwnProperty.call(this.#options, key)) {
                clone[key] = this.#options[key];
            }
        }

        return clone;
    }

    /**
     * Get current pan position.
     * @returns {Object} - Current pan position.
     */
    getPan() {
        return { x: this.#x, y: this.#y };
    }

    /**
     * Get current scale.
     * @returns {number} - Current scale.
     */
    getScale() {
        return this.#scale;
    }

    /**
     * Get whether the panzoom is paused.
     * @returns {Boolean} - True when pauseed, false if not.
     */
    isPaused() {
        return this.#paused;
    }

    /**
     * Pan the element to a new position.
     * @param {number} toX - Target X position.
     * @param {number} toY - Target Y position.
     * @param {Event} originalEvent - Original event that triggered this.
     * @returns {Object} - Current state object.
     */
    pan(toX, toY, originalEvent) {
        // Always use updated constrain that correctly applies containment with scale.
        const result = this.#constrain(toX, toY, this.#scale);

        // Only update if values changed.
        if (this.#x !== result.x || this.#y !== result.y) {
            this.#x = result.x;
            this.#y = result.y;

            return this.#setTransformWithEvent('panzoompan', originalEvent);
        }

        return { originalEvent, scale: this.#scale, x: this.#x, y: this.#y };
    }

    /**
     * Pause events.
     */
    pause() {
        this.destroy();
        this.#paused = true;
    }

    /**
     * Reset to initial state.
     * @param {Object} resetOptions - Reset options.
     * @returns {Object} - Current state object.
     */
    reset(resetOptions = {}) {
        const options = { ...this.#options, ...resetOptions };

        // Ensure scale is within bounds when resetting.
        this.#scale = Math.min(Math.max(options.startScale, this.#options.minScale), this.#options.maxScale);

        const constrainResult = this.#constrain(options.startX, options.startY, this.#scale);

        this.#x = constrainResult.x;
        this.#y = constrainResult.y;

        this.#resetState();

        return this.#setTransformWithEvent('panzoomreset');
    }

    /**
     * Resume after pause.
     */
    resume() {
        if (this.#paused) {
            this.listen();
            this.#paused = false;
        }
    }

    /**
     * Update options with new values.
     * @param {Object} options - New options.
     */
    setOptions(options = {}) {
        for (const key in options) {
            if (Object.prototype.hasOwnProperty.call(options, key)) {
                this.#options[key] = options[key];
            }
        }

        // If maxScale changed and current scale is higher, adjust it.
        if (Object.prototype.hasOwnProperty.call(options, 'maxScale') && this.#scale > this.#options.maxScale) {
            this.zoom(this.#options.maxScale);
        }

        // If minScale changed and current scale is lower, adjust it.
        if (Object.prototype.hasOwnProperty.call(options, 'minScale') && this.#scale < this.#options.minScale) {
            this.zoom(this.#options.minScale);
        }

        // Handle option side-effects.
        if (Object.prototype.hasOwnProperty.call(options, 'cursor') || Object.prototype.hasOwnProperty.call(options, 'useParent')) {
            // Reset current cursors.
            this.#parent.style.cursor = '';
            this.#elem.style.cursor = '';

            // Apply to selected container.
            (this.#options.useParent ? this.#parent : this.#elem).style.cursor = this.#options.cursor;
        }
    }

    /**
     * Zoom the element to a new scale.
     * @param {number} toScale - Target scale.
     * @param {Event} originalEvent - Original event that triggered this.
     * @param {Object} zoomOptions - Zoom options.
     * @returns {Object} - Current state object.
     */
    zoom(toScale, zoomOptions = {}, originalEvent = null) {
        const options = { ...this.#options, ...zoomOptions };

        // Ensure scale is within bounds.
        const newScale = Math.min(Math.max(toScale, this.#options.minScale), this.#options.maxScale);

        // Calculate new position based on focal point if provided.
        let toX = this.#x,
            toY = this.#y;

        if (options.focal) {
            const { focal } = options;
            const rect = this.#elem.getBoundingClientRect();

            // Calculate relative focal position within the element.
            const focalRelativeX = (focal.clientX - rect.left) / rect.width;
            const focalRelativeY = (focal.clientY - rect.top) / rect.height;

            // Get original dimensions.
            const originalWidth = parseFloat(this.#elem.dataset.originalWidth);
            const originalHeight = parseFloat(this.#elem.dataset.originalHeight);

            // Calculate current and future sizes.
            const currentWidth = originalWidth * this.#scale;
            const currentHeight = originalHeight * this.#scale;
            const newWidth = originalWidth * newScale;
            const newHeight = originalHeight * newScale;

            // Adjust position to keep focal point fixed.
            toX = this.#x - ((newWidth - currentWidth) * focalRelativeX);
            toY = this.#y - ((newHeight - currentHeight) * focalRelativeY);
        }

        // Apply containment.
        if (options.contain) {
            const constrainResult = this.#constrain(toX, toY, newScale);

            toX = constrainResult.x;
            toY = constrainResult.y;
        }

        this.#x = toX;
        this.#y = toY;
        this.#scale = newScale;

        return this.#setTransformWithEvent('panzoomzoom', originalEvent);
    }

    /**
     * Zoom in by a factor.
     * @param {Object} zoomOptions - Zoom options.
     * @returns {Object} - Current state object.
     */
    zoomIn(zoomOptions) {
        return this.zoomInOut(true, zoomOptions);
    }

    /**
     * Zoom in or out by a factor.
     * @param {boolean} isIn - True to zoom in, false to zoom out.
     * @param {Object} zoomOptions - Zoom options.
     * @returns {Object} - Current state object.
     */
    zoomInOut(isIn, zoomOptions = {}) {
        const options = { ...this.#options, ...zoomOptions };
        const direction = isIn ? 1 : -1;

        // Calculate target scale with exponential factor.
        let targetScale = this.#scale * Math.exp(direction * options.step);

        // Pre-constrain.
        targetScale = Math.min(targetScale, this.#options.maxScale);

        return this.zoom(targetScale, options);
    }

    /**
     * Zoom out by a factor.
     * @param {Object} zoomOptions - Zoom options.
     * @returns {Object} - Current state object.
     */
    zoomOut(zoomOptions) {
        return this.zoomInOut(false, zoomOptions);
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Add a pointer event to the tracking array.
     * @param {Array} pointers - Array to add the pointer to.
     * @param {Event} event - Pointer event to add.
     * @private
     */
    #addPointer(pointers, event) {
        // For touch events, add the first two touches with their actual identifiers.
        if (event.touches) {
            if (pointers.length < 2) {
                const touchesToProcess = Math.min(2 - pointers.length, event.touches.length);
                for (let i = 0; i < touchesToProcess; i++) {
                    const touch = event.touches[i];
                    const pointer = {
                        clientX: touch.clientX,
                        clientY: touch.clientY,
                        identifier: touch.identifier
                    };
                    pointers.push(pointer);
                    this.#touchesById.set(touch.identifier, pointer);
                }
            }

            return;
        }

        // For mouse/pointer events.
        const index = this.#findEventIndex(pointers, event);

        if (index > -1) {
            pointers[index] = event;
        }
        else if (pointers.length < 2) {
            pointers.push(event);
        }
    }

    /**
     * Constrain pan values based on current scale and container dimensions.
     * @param {number} toX - Target X position.
     * @param {number} toY - Target Y position.
     * @param {number} scale - The scale to use.
     * @returns {Object} - Constrained x and y values.
     * @private
     */
    #constrain(toX, toY, scale) {
        const rectParent = this.#parent.getBoundingClientRect();

        // Get original dimensions.
        const originalWidth = parseFloat(this.#elem.dataset.originalWidth);
        const originalHeight = parseFloat(this.#elem.dataset.originalHeight);

        // Calculate the scaled dimensions.
        const scaledWidth = originalWidth * scale;
        const scaledHeight = originalHeight * scale;

        // Calculate min/max constraints.
        let maxX, maxY, minX, minY;

        // If the scaled element is smaller than the parent, center it.
        if (scaledWidth <= rectParent.width) {
            const left = (rectParent.width - scaledWidth) / 2;
            minX = left;
            maxX = left;
        }
        else {
            minX = rectParent.width - scaledWidth;
            maxX = 0;
        }

        if (scaledHeight <= rectParent.height) {
            const top = (rectParent.height - scaledHeight) / 2;
            minY = top;
            maxY = top;
        }
        else {
            minY = rectParent.height - scaledHeight;
            maxY = 0;
        }

        // Apply constraints.
        return {
            x: Math.min(Math.max(toX, minX), maxX),
            y: Math.min(Math.max(toY, minY), maxY)
        };
    }

    /**
     * Find the index of a pointer event in an array.
     * @param {Array} pointers - Array of pointer events.
     * @param {Event} event - The event to find.
     * @returns {number} - Index of the event or -1 if not found.
     * @private
     */
    #findEventIndex(pointers, event) {
        let i = pointers.length;

        while (i--) {
            if (pointers[i].pointerId === event.pointerId) {
                return i;
            }
        }

        return -1;
    }

    /**
     * Calculates the distance between two points for pinch zooming.
     * @param {Array} pointers - Array of pointer events.
     * @returns {number} - Distance between points.
     * @private
     */
    #getDistance(pointers) {
        if (pointers.length < 2) {
            return 0;
        }

        const event1 = pointers[0];
        const event2 = pointers[1];

        return Math.sqrt((Math.abs(event2.clientX - event1.clientX) ** 2) + (Math.abs(event2.clientY - event1.clientY) ** 2));
    }

    /**
     * Calculates a center point between multiple pointers.
     * @param {Array} pointers - Array of pointer events.
     * @returns {Object} - Object with clientX and clientY coordinates.
     * @private
     */
    #getMiddle(pointers) {
        // Copy to avoid changing by reference.
        const pointersClone = pointers.slice(0);

        let event1 = pointersClone.pop(),
            event2;

        while ((event2 = pointersClone.pop())) {
            event1 = {
                clientX: ((event2.clientX - event1.clientX) / 2) + event1.clientX,
                clientY: ((event2.clientY - event1.clientY) / 2) + event1.clientY
            };
        }

        return event1;
    }

    /**
     * Remove a pointer event from the tracking array.
     * @param {Array} pointers - Array to remove pointer from.
     * @param {Event} event - Pointer event to remove.
     * @private
     */
    #removePointer(pointers, event) {
        // Handle touches if applicable.
        if (event.touches || event.changedTouches) {
            // Clean up touch entries on end/cancel.
            const changedTouches = event.changedTouches || event.touches;
            for (const touch of changedTouches) {
                this.#touchesById.delete(touch.identifier);
            }

            // Remove all touches from pointers array.
            while (pointers.length) {
                pointers.pop();
            }

            return;
        }

        const i = this.#findEventIndex(pointers, event);

        if (i > -1) {
            pointers.splice(i, 1);
        }
    }

    /**
     * Everything back to intial.
     */
    #resetState() {
        // Reset state including all touch state.
        this.#isPanning = false;
        this.#origX = undefined;
        this.#origY = undefined;
        this.#pointers = [];
        this.#startClientX = undefined;
        this.#startClientY = undefined;
        this.#startDistance = 0;
        this.#startScale = undefined;
        this.#touchesById.clear();
        this.#lastTimestamp = performance.now();
    }

    /**
     * Schedules a single `panzoomend` to fire once wheel panning settles, resetting the timer on each wheel event so it fires only after the stream stops.
     * @param {WheelEvent} event - The wheel event that triggered the pan.
     *
     * @private
     */
    #scheduleWheelPanEnd(event) {
        if (this.#wheelEndTimer !== null) {
            clearTimeout(this.#wheelEndTimer);
        }

        this.#wheelEndTimer = setTimeout(() => {
            this.#wheelEndTimer = null;
            this.#trigger('panzoomend', { originalEvent: event, scale: this.#scale, x: this.#x, y: this.#y });
        }, window.Panzoom.WHEEL_PAN_END_DELAY);
    }

    /**
     * Apply transformation and trigger events.
     * @param {string} eventName - Name of the event to trigger.
     * @param {Event} originalEvent - Original event that triggered this.
     * @returns {Object} - Current state object.
     * @private
     */
    #setTransformWithEvent(eventName, originalEvent) {
        const value = { originalEvent, scale: this.#scale, x: this.#x, y: this.#y };

        requestAnimationFrame(() => {
            try {
                // Get original dimensions.
                const originalWidth = parseFloat(this.#elem.dataset.originalWidth);
                const originalHeight = parseFloat(this.#elem.dataset.originalHeight);

                // Apply scale.
                this.#elem.style.inlineSize = `${originalWidth * this.#scale}px`;
                this.#elem.style.blockSize = `${originalHeight * this.#scale}px`;

                // Apply translate.
                this.#elem.style.insetInlineStart = `${this.#x}px`;
                this.#elem.style.insetBlockStart = `${this.#y}px`;

                this.#trigger(eventName, value);
                this.#trigger('panzoomchange', value);
            }
            catch {
                // Silently fail to prevent breaking gesture handling.
            }
        });

        return value;
    }

    /**
     * Trigger custom events.
     * @param {string} eventName - Name of the event to trigger.
     * @param {Object} detail - Event detail data.
     * @private
     */
    #trigger(eventName, detail) {
        const event = new CustomEvent(eventName, { detail });
        this.#elem.dispatchEvent(event);
    }

    /**
     * Update a pointer event in the tracking array.
     * @param {Array} pointers - Array of pointers.
     * @param {Event} event - Pointer event to update.
     * @private
     */
    #updatePointer(pointers, event) {
        const index = this.#findEventIndex(pointers, event);

        if (index > -1) {
            pointers[index] = event;
        }
    }
    // #endregion ********************************************************************************/
};